import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2, RefreshCw } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";

const AdminCalendar = () => {
  const [integ, setInteg] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [logs, setLogs] = useState<any[]>([]);
  const [selected, setSelected] = useState<any | null>(null);

  const load = async () => {
    const [{ data: integRow }, { data: logRows }] = await Promise.all([
      supabase.from("shared_calendar_integration").select("*").eq("id", 1).maybeSingle(),
      supabase.from("calendar_sync_log" as any).select("*").order("created_at", { ascending: false }).limit(25),
    ]);
    setInteg(integRow);
    setLogs((logRows as any[]) ?? []);
    setLoading(false);
  };
  useEffect(() => { document.title = "Calendar | Admin"; load(); }, []);

  const connect = async () => {
    try {
      const { data, error } = await supabase.functions.invoke("gcal-shared-connect", { body: {} });
      if (error) { toast.error(error.message); return; }
      if (data?.error) { toast.error(data.error); return; }
      toast.success(`Connected ${data?.email ?? ""}`);
      await load();
    } catch (e: any) {
      toast.error(e?.message ?? "Network error");
    }
  };

  const syncNow = async () => {
    setSyncing(true);
    const { data, error } = await supabase.functions.invoke("gcal-shared-sync", { body: {} });
    if (error) toast.error(error.message);
    else toast.success(data?.message ?? `Synced ${data?.count ?? 0} bookings`);
    await load();
    setSyncing(false);
  };

  if (loading) return <div className="pt-32 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>;

  return (
    <div className="pt-24 container mx-auto px-4 max-w-2xl pb-16">
      <h1 className="font-display text-2xl tracking-widest mb-2">SHARED GOOGLE CALENDAR</h1>
      <p className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-8">All bookings sync to this account's calendar</p>

      <div className="bg-panel border border-border p-6 space-y-4">
        <div className="flex justify-between"><span className="font-mono text-xs uppercase text-muted-foreground">Status</span><span className={integ?.refresh_token ? "text-primary" : "text-muted-foreground"}>{integ?.refresh_token ? "CONNECTED" : "NOT CONNECTED"}</span></div>
        <div className="flex justify-between"><span className="font-mono text-xs uppercase text-muted-foreground">Account</span><span className="font-mono text-xs">{integ?.google_email ?? "—"}</span></div>
        <div className="flex justify-between"><span className="font-mono text-xs uppercase text-muted-foreground">Last sync</span><span className="font-mono text-xs">{integ?.last_sync_at ? new Date(integ.last_sync_at).toLocaleString() : "Never"}</span></div>

        <div className="flex gap-2 pt-4 border-t border-border">
          <Button onClick={connect} variant={integ?.refresh_token ? "outline" : "default"} className="flex-1">
            {integ?.refresh_token ? "Re-verify connector" : "Verify Lovable Cloud connector"}
          </Button>
          <Button onClick={syncNow} disabled={syncing} variant="outline">
            {syncing ? <Loader2 className="w-4 h-4 animate-spin" /> : <><RefreshCw className="w-4 h-4 mr-2" />Sync now</>}
          </Button>
        </div>
      </div>

      <p className="mt-6 text-xs font-mono text-muted-foreground">
        This calendar uses the <strong>Lovable Cloud Google Calendar connector</strong>. The currently linked account ({integ?.google_email ?? "unverified"}) receives all booking events on its primary calendar. Manage the connector under Cloud → Connectors.
      </p>

      <div className="mt-10">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display text-sm tracking-widest">RECENT SYNC ACTIVITY</h2>
          <button onClick={load} className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground hover:text-primary">Refresh</button>
        </div>
        <div className="bg-panel border border-border divide-y divide-border">
          {logs.length === 0 && (
            <div className="p-4 text-xs font-mono text-muted-foreground">No sync activity yet.</div>
          )}
          {logs.map((l) => (
            <button
              key={l.id}
              onClick={() => setSelected(l)}
              className="w-full text-left p-3 text-xs font-mono flex flex-col gap-1 hover:bg-background/50 transition-colors"
            >
              <div className="flex justify-between gap-2">
                <span className="uppercase">{l.action}</span>
                <span className={l.status === "ok" ? "text-primary" : l.status === "error" ? "text-destructive" : "text-muted-foreground"}>
                  {l.status}
                </span>
                <span className="text-muted-foreground">{new Date(l.created_at).toLocaleString()}</span>
              </div>
              {l.booking_id && <div className="text-muted-foreground truncate">booking: {l.booking_id}</div>}
              {l.error && <div className="text-destructive break-all">{l.error}</div>}
            </button>
          ))}
        </div>
      </div>

      <Sheet open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <SheetContent className="w-full sm:max-w-xl overflow-y-auto bg-panel border-l border-border">
          <SheetHeader>
            <SheetTitle className="font-display tracking-widest">
              {selected?.action?.toUpperCase()} · <span className={selected?.status === "ok" ? "text-primary" : selected?.status === "error" ? "text-destructive" : "text-muted-foreground"}>{selected?.status}</span>
            </SheetTitle>
            <SheetDescription className="font-mono text-xs">
              {selected ? new Date(selected.created_at).toLocaleString() : ""}
            </SheetDescription>
          </SheetHeader>
          {selected && (
            <div className="mt-6 space-y-5 text-xs font-mono">
              <Field label="Booking ID" value={selected.booking_id ?? "—"} />
              <Field label="Google Event ID" value={selected.google_event_id ?? "—"} />
              {selected.error && (
                <div>
                  <div className="text-muted-foreground uppercase mb-1">Error</div>
                  <div className="bg-background border border-destructive/40 p-3 text-destructive break-all whitespace-pre-wrap">{selected.error}</div>
                </div>
              )}
              {selected.details?.attempts?.length > 0 && (
                <div>
                  <div className="text-muted-foreground uppercase mb-1">Retry attempts ({selected.details.attempts.length})</div>
                  <div className="bg-background border border-border divide-y divide-border">
                    {selected.details.attempts.map((a: any, i: number) => (
                      <div key={i} className="p-2 flex justify-between gap-2">
                        <span>#{a.attempt}</span>
                        <span className={a.status && a.status < 400 ? "text-primary" : "text-destructive"}>{a.status ?? a.error}</span>
                        <span className="text-muted-foreground">{a.ms}ms</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {selected.details?.request && (
                <Block label="Request body" value={selected.details.request} />
              )}
              {selected.details?.response_snippet && (
                <div>
                  <div className="text-muted-foreground uppercase mb-1">Response snippet</div>
                  <pre className="bg-background border border-border p-3 overflow-x-auto whitespace-pre-wrap break-all">{selected.details.response_snippet}</pre>
                </div>
              )}
              {selected.details?.booking && (
                <Block label="Booking context" value={selected.details.booking} />
              )}
              <Block label="Full details JSON" value={selected.details ?? {}} />
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
};

const Field = ({ label, value }: { label: string; value: string }) => (
  <div className="flex justify-between gap-2 border-b border-border pb-1">
    <span className="text-muted-foreground uppercase">{label}</span>
    <span className="break-all text-right">{value}</span>
  </div>
);

const Block = ({ label, value }: { label: string; value: unknown }) => (
  <div>
    <div className="text-muted-foreground uppercase mb-1">{label}</div>
    <pre className="bg-background border border-border p-3 overflow-x-auto whitespace-pre-wrap break-all text-[11px]">{JSON.stringify(value, null, 2)}</pre>
  </div>
);

export default AdminCalendar;